import { mentionFactory } from "../../../ui/sked-editor/social-mention/mention-decorator-node";
import { MentionsPlugin } from "../../../ui/sked-editor/social-mention/mention-plugin";
import { ColCollaborator } from "../types";
import { SkedEditor } from "../../../ui/sked-editor/sked-editor";
import { cn } from "../../../lib/utils";
import { useEffect, useMemo, useRef } from "react";
import { regexConversionFactory } from "../../../ui/sked-editor/editor-state-conversion";
import {
  SkedMentionBox,
  SkedMentionItem,
} from "../../../ui/SkedCaption/v2/sked-caption-plugins/sked-mention-box";

export const {
  $createMentionNode: $createColMentionNode,
  NodeClass: ColMentionNode,
  $isMentionNode: $isColMentionNode,
} = mentionFactory({
  nodeName: "col-mention-type",
  renderNode: (node, { isSelected }) => {
    return (
      <span
        className={`inline ${isSelected ? "outline-dotted outline-blue-500" : ""} relative  text-blue-500 cursor-default font-semibold`}
      >
        {node.__text}
      </span>
    );
  },
});

export const $colTextImport = (collaborators: ColCollaborator[]) => {
  const colMap = new Map<string, ColCollaborator>();
  for (const c of collaborators) {
    if (c._id) {
      colMap.set(c._id, c);
    }
  }
  return regexConversionFactory({
    regex: /@\[(\w*)\]/gm,
    func: (match) => {
      const id = match[1];
      const found = colMap.get(id);
      if (!found) {
        return null;
      }
      return $createColMentionNode(match[0], getDisplayName(found));
    },
  });
};

export const CollaborationInput = ({
  collaborators,
  onChange,
  value,
  onSubmit,
  disabled,
  isExternal,
  isError,
  className,
}: {
  collaborators: ColCollaborator[];
  onChange?: (value: string) => void;
  value: string | null | undefined;
  onSubmit: () => void;
  disabled?: boolean;
  isExternal: boolean;
  isError: boolean;
  className?: string;
}) => {
  const colTextImport = useMemo(() => {
    return $colTextImport(collaborators);
  }, [collaborators]);
  const htmlRef = useRef<HTMLDivElement>(null);
  const contentHtmlRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    requestAnimationFrame(() => {
      if (htmlRef.current && contentHtmlRef.current) {
        if (htmlRef.current.clientHeight > 72) {
          htmlRef.current.style.height = "5px";
        }
        if (contentHtmlRef.current.scrollHeight > 72) {
          htmlRef.current.style.height = `${contentHtmlRef.current.scrollHeight}px`;
        }
      }
    });
  }, [value]);

  return (
    <SkedEditor
      htmlRef={htmlRef}
      contentHtmlRef={contentHtmlRef}
      style={{ height: 72 }}
      disabled={disabled}
      placeholder="Type a message or @ to mention a user ..."
      onEnterSubmit={onSubmit}
      input={{
        text: value || "",
        textImportProcess: colTextImport,
      }}
      placeholderClassName="px-[8px] py-[8px] text-[14px] text-[12px]"
      className={cn(
        "border border-[var(--collab-border)]",
        "rounded-[8px]",
        "min-h-[72px] max-h-[160px]",
        "px-[8px] py-[8px]",
        "resize-none",
        "bg-[var(--collab-surface)]",
        "text-[12px] leading-[16px] text-[#333]",
        "transition-[border-color,box-shadow,background] duration-200 ease-linear",
        "disabled:bg-[var(--color-neutral-50)] disabled:cursor-not-allowed",
        {
          "data-focused:border-yellow-500": isExternal,
          "border-red-500": isError,
          "data-focused:border-red-500": isError,
        },
        className
      )}
      onCaptionChange={(c) => onChange?.(c)}
      plugins={
        <>
          <ColMentionPlugin collaborators={collaborators} />
        </>
      }
      initNodes={[ColMentionNode]}
    />
  );
};

const ColMentionPlugin = ({
  collaborators,
}: {
  collaborators: ColCollaborator[];
}) => {
  return (
    <MentionsPlugin
      lookupConfig={{
        allowWhitespace: true,
        typeDebounceTime: 50,
        cache: false,
      }}
      menuBoxBehavior={{
        showLoading: false,
        nodeConversion: {
          type: "mention-node",
          trailingSpace: true,
          convertFunc: (item) => {
            return {
              platformStructureText: `@[${item.key}]`,
              displayText: item.displayName,
            };
          },
          $createMentionNode: $createColMentionNode,
        },
        dataFetcher: async (
          lookupText: string | null | undefined
        ): Promise<SkedMentionItem[]> => {
          const rs = lookupText?.trim()
            ? collaborators.filter((c) =>
                getDisplayName(c)
                  .toLowerCase()
                  .includes((lookupText || "").toLowerCase())
              )
            : collaborators;
          return rs
            .map((c) => ({
              key: c._id || "",
              displayName: getDisplayName(c),
              profileImg: c.profileImage,
            }))
            .filter((c) => c.key);
        },
        renderMenuBox: (props, { lookupText }) => {
          return (
            <SkedMentionBox
              {...props}
              displayConfig={{
                showProfileImage: true,
              }}
              highlightBehavior={{
                matchingOn: "contains",
                searchText: lookupText,
              }}
            />
          );
        },
      }}
    />
  );
};
const getDisplayName = (c: ColCollaborator) => {
  return `${c.firstName || ""} ${c.lastName || ""}`;
};
